import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { searchLocations } from '../api/rankings';
import { MapPin, ChevronDown, X } from 'lucide-react';
import { cn } from '../lib/utils';

export default function LocationPicker({ value, onChange, className }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const ref = useRef(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ['serp-locations', debouncedSearch],
    queryFn: () => searchLocations({ q: debouncedSearch }),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const locations = data?.locations || [];

  const typeColors = {
    Country: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    State: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    City: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    'DMA Region': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    Region: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  };

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-border bg-background text-sm hover:bg-muted transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="truncate">
            {value ? `${value.location_name} (${value.location_code})` : 'Select location...'}
          </span>
        </div>
        {value ? (
          <X
            className="h-4 w-4 text-muted-foreground hover:text-foreground flex-shrink-0"
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
          />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        )}
      </button>

      {open && (
        <div className="absolute z-50 top-full mt-1 w-full bg-background border border-border rounded-lg shadow-lg max-h-64 overflow-hidden">
          <div className="p-2 border-b border-border">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search locations..."
              className="w-full px-3 py-1.5 text-sm bg-muted rounded-md border-0 outline-none focus:ring-1 focus:ring-primary"
              autoFocus
            />
          </div>
          <div className="overflow-y-auto max-h-48">
            {isLoading ? (
              <div className="px-3 py-4 text-sm text-muted-foreground text-center">Loading...</div>
            ) : locations.length === 0 ? (
              <div className="px-3 py-4 text-sm text-muted-foreground text-center">No locations found</div>
            ) : (
              locations.map((loc) => (
                <button
                  key={loc.location_code}
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors text-left"
                  onClick={() => {
                    onChange({ location_code: loc.location_code, location_name: loc.location_name });
                    setOpen(false);
                    setSearch('');
                  }}
                >
                  <span className="flex-1 truncate">{loc.location_name}</span>
                  {loc.location_type && (
                    <span className={cn('text-xs px-1.5 py-0.5 rounded-full flex-shrink-0', typeColors[loc.location_type] || 'bg-muted text-muted-foreground')}>
                      {loc.location_type}
                    </span>
                  )}
                  {loc.country_iso_code && (
                    <span className="text-xs text-muted-foreground flex-shrink-0">{loc.country_iso_code}</span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
